"use client";

import React from "react";
import { Mail, ArrowRight, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NewsletterFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-2xl bg-primary/5 p-8 text-center border border-primary/10">
        <div className="rounded-lg bg-primary/10 p-3">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="font-default text-xl font-semibold text-foreground">
            Thank you!
          </h3>
          <p className="text-muted-foreground">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md">
          Subscribe another email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="w-full space-y-5 text-left">
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" className="bg-background border-input rounded-md" {...field} value={field.value || ""} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-default">
                Email Address <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="your@email.com" className="bg-background border-input rounded-md" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground">
                We'll send you updates on our global impact
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="consent" rules={{
      required: "Please accept to continue"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-border bg-background/50 p-4 shadow-sm">
            <FormControl>
              <input type="checkbox" className="mt-1 size-4 shrink-0 rounded-sm border-primary text-primary focus:ring-primary accent-primary" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-medium text-foreground cursor-pointer font-default">
                I agree to receive updates and impact reports <span className="text-destructive">*</span>
              </FormLabel>
              <FormDescription className="text-xs text-muted-foreground">
                Confirm you'd like to stay informed about our mission
              </FormDescription>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto sm:min-w-[200px] h-12 text-base font-semibold shadow-md bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-all hover:scale-[1.02]" size="lg">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Processing...
          </> : <>
            Join the Movement
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function JoinOurNewsletter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section data-section-id="224896" id="join-our-newsletter" className="relative w-full overflow-hidden bg-background py-24">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] mix-blend-multiply" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 100c50 0 50-50 100-50s50 50 100 50 50-50 100-50 50 50 100 50M0 200c50 0 50-50 100-50s50 50 100 50 50-50 100-50 50 50 100 50M0 300c50 0 50-50 100-50s50 50 100 50 50-50 100-50 50 50 100 50' stroke='%233b82f6' fill='none' stroke-width='1'/%3E%3C/svg%3E")`
    }} />
      
      <div className="absolute inset-0 pointer-events-none flex justify-center items-center overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-lg" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div ref={ref} className={`mx-auto max-w-3xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col items-center text-center space-y-8 rounded-3xl border border-border/40 bg-card/30 p-8 md:p-12 shadow-lg backdrop-blur-md">
            
            <div className="space-y-4 max-w-2xl">
              <div className="inline-flex items-center rounded-lg border border-secondary/20 bg-secondary/10 px-3 py-1 text-sm font-semibold text-secondary">
                <Mail className="mr-2 size-4" />
                Stay in the Loop
              </div>
              <h2 className="font-default text-3xl font-black tracking-tighter text-foreground sm:text-4xl md:text-5xl">
                Join Our <span className="text-primary">Global Community</span>
              </h2>
              <p className="text-lg text-muted-foreground font-default leading-relaxed">
                Subscribe to receive field reports, success stories, and urgent updates on how your support is changing lives across the globe.
              </p>
            </div>

            <div className="w-full max-w-xl mx-auto">
              <FormProvider formKey="newsletter_signup_field_reports" formId="newsletter_signup_field_reports" sectionName="join-our-newsletter" defaultValues={{
              email: "",
              first_name: "",
              consent: false
            }} validationRules={{
              email: {
                required: true,
                pattern: "email"
              },
              consent: {
                required: true
              }
            }} submitText="Join the Movement" successMessage="Welcome to the foundation! You're now subscribed to our impact updates." errorMessage="Something went wrong. Please try again to join our community.">
                <NewsletterFormInner />
              </FormProvider>
            </div>
            
            <p className="text-xs text-muted-foreground max-w-md mx-auto font-default">
              By joining, you agree to our privacy policy. We value your trust and will never share your information with third parties.
            </p>
          </div>
        </div>
      </div>
    </section>;
}